/**
 * 未出貨訂單-客人 Back-Order recognition (S11.1).
 *
 * THE anti-divergence contract: the Back-Order report figure MUST equal the
 * OCC 待出量 number. Both are computed from the SAME shared selector
 * `selectOrderBalanceTotal` in `src/lib/occ/selectors.ts` over the SAME
 * projected `OccLensLine[]` — so OCC and the report can never disagree BY
 * CONSTRUCTION (plan §S11.1 accept: `OCC 待出量 == BO report figure`).
 *
 * Recognition cutoff is a CONFIGURABLE predicate `isRecognizedBO(line,
 * cutoffDate)` — the cutoff RULE itself is still pending 林芊 (appendix B),
 * so the report renders a visible "rule pending" caveat and, until a rule is
 * stated, provisionally recognizes every line that still carries an open
 * 待出量 remainder. With `cutoffDate === null` the recognized set therefore
 * differs from the full lens set only by lines whose 待出量 is already zero
 * (they contribute 0 to the sum anyway) — so the provisional total equals the
 * OCC 待出量 total exactly.
 */

import { selectOrderBalanceTotal, type OccLensLine } from '@/lib/occ/selectors';

/**
 * Is a fulfillment line a recognized customer back-order as at `cutoffDate`?
 *
 * - A line with no open 待出量 remainder (`qtyOpenToLoad <= 0`) is never a
 *   back-order — the work queue is empty for it.
 * - `cutoffDate === null` = rule pending 林芊: provisionally recognize every
 *   line that still has an open remainder (report flagged not-trustworthy via
 *   the caveat banner until the real rule lands).
 * - When a cutoff IS supplied, recognize only lines last moved on or before
 *   it. The only deterministic date fact on the projected lens line is
 *   `updatedAt`; the container-date cutoff the client described (6/10櫃 vs
 *   6/9不認列) plugs in here once 林芊 states the exact date field.
 */
export function isRecognizedBO(line: OccLensLine, cutoffDate: string | null): boolean {
  if (line.qtyOpenToLoad <= 0) return false;
  if (cutoffDate === null) return true;
  return line.updatedAt <= cutoffDate;
}

/** The recognized back-order lines for a cutoff (rule pending → all open lines). */
export function selectBackOrderLines(
  lines: OccLensLine[],
  cutoffDate: string | null
): OccLensLine[] {
  return lines.filter((line) => isRecognizedBO(line, cutoffDate));
}

/**
 * THE Back-Order 待出量 figure — Σ `qtyOpenToLoad` over the recognized lines,
 * via the SHARED OCC selector. With `cutoffDate === null` this equals
 * `selectOrderBalanceTotal(lines)` (the OCC board's 待出量 total) because the
 * only excluded lines already contribute 0 — the one-record-many-lenses
 * guarantee the S11.1 vitest asserts.
 */
export function selectBackOrderTotal(lines: OccLensLine[], cutoffDate: string | null): number {
  return selectOrderBalanceTotal(selectBackOrderLines(lines, cutoffDate));
}
